import LoadingLogo from '@/components/atoms/LoadingLogo';
import CustomButton from '@/components/atoms/CustomButton';
import useRecoverContentCreatorApplication from '@/hooks/contentcreator/useRecoverContentCreatorApplication';
import useVerifyContentCreatorApplication from '@/hooks/contentcreator/useVerifyContentCreatorApplication';
import { Text, VStack } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import CreatorApplicationForm from './CreatorApplicationForm';
import type { ContentFocusValue } from './ContentFocusTile';
import PendingApplicationDetails from './PendingApplicationDetails';
import RejectedApplicationDetails from './RejectedApplicationDetails';
import { useCreatorApplicationStore } from '@/store/useCreatorApplicationStore';
import type { CreatorApplicationPreviewData } from '@/store/useCreatorApplicationStore';
import type {
  TContentCreatorApplicationSummary,
  TPublicContentCreatorApplicationMe,
} from '@/services/api/requests/contentCreatorApplications';
import { creatorSocialMediaFields } from './creatorSocialLinks';
import ApprovedApplicationDetails from './ApprovedApplicationDetails';

type Props = {
  email: string;
  token: string;
  application?: TPublicContentCreatorApplicationMe;
  isApplicationError?: boolean;
  isApplicationLoading?: boolean;
};

const buildPreviewDataFromRejectedApplication = (
  rejectedApplication: TContentCreatorApplicationSummary,
): CreatorApplicationPreviewData => {
  const profile = rejectedApplication.profile;

  return {
    applicantName: rejectedApplication.applicantName,
    profilePhoto: null,
    creatorName: profile.creatorName ?? '',
    keepNameSameAsRealName: profile.keepNameSameAsRealName ?? true,
    existingProfilePhotoUrl: profile.photoUrl ?? null,
    profilePhotoPreview: profile.photoUrl ?? null,
    aboutMe: profile.bio ?? '',
    city: profile.location?.city ?? '',
    zipCode: profile.location?.zip ?? '',
    state: profile.location?.state ?? '',
    socialMediaHandles: creatorSocialMediaFields.map(
      ({ platform }) => profile.socialLinks?.find((socialLink) => socialLink.platform === platform)?.url ?? '',
    ),
    qualifications: profile.certifications ?? [],
    contentFocus: (profile.contentFocus ?? []) as ContentFocusValue[],
  };
};

const ContentCreatorApplicationPublicHero = ({
  email,
  token,
  application,
  isApplicationError = false,
  isApplicationLoading = false,
}: Props) => {
  const handledRef = useRef(false);
  const setPublicCredentials = useCreatorApplicationStore((state) => state.setPublicCredentials);
  const setPreviewData = useCreatorApplicationStore((state) => state.setPreviewData);

  const [isSuccess, setIsSuccess] = useState<boolean | null>(null);
  const [isReapplying, setIsReapplying] = useState(false);

  const pendingApplication = useMemo(() => {
    if (application?.currentApplication?.status === 'pending') {
      return application.currentApplication;
    }

    return null;
  }, [application]);

  const approvedApplication = useMemo(() => {
    const firstHistoryApplication = application?.history?.[0];

    if (application?.currentApplication === null && firstHistoryApplication?.status === 'approved') {
      return firstHistoryApplication;
    }

    return null;
  }, [application]);

  const rejectedApplication = useMemo(() => {
    const firstHistoryApplication = application?.history?.[0];

    if (application?.currentApplication === null && firstHistoryApplication?.status === 'rejected') {
      return firstHistoryApplication;
    }

    return null;
  }, [application]);

  const {
    mutate: verifyContentCreatorApplication,
    isPending,
    errorMessage,
    isError,
  } = useVerifyContentCreatorApplication((success) => {
    if (success) {
      setPublicCredentials({ email, token });
      setIsSuccess(true);
    } else {
      setIsSuccess(false);
    }
  });

  const { mutate: recoverContentCreatorApplication, isPending: isRecoveringApplicationLink } =
    useRecoverContentCreatorApplication();

  const isVerificationSuccess = isSuccess === true;
  const isVerificationFailed = isError || isSuccess === false;
  const isVerifying = isPending && !isVerificationSuccess && !isVerificationFailed;

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    verifyContentCreatorApplication({ email, token });
  }, [email, token, verifyContentCreatorApplication]);

  useEffect(() => {
    if (!isReapplying) return;

    const handleBrowserBack = () => {
      setIsReapplying(false);
    };

    window.addEventListener('popstate', handleBrowserBack);

    return () => {
      window.removeEventListener('popstate', handleBrowserBack);
    };
  }, [isReapplying]);

  if (isVerificationSuccess && isApplicationLoading) {
    return (
      <VStack minHeight={'100vh'} width={'100vw'} justifyContent={'center'} paddingX={'24px'} spacing={'24px'}>
        <Text variant={'loraTitle'} color={'white'} fontSize={'26px'} lineHeight={'32px'} fontWeight={'400'}>
          Loading Application
        </Text>
        <LoadingLogo variant="light" spinnerSize="60px" width="90px" />
      </VStack>
    );
  }

  if (isVerificationSuccess && isReapplying) {
    return <CreatorApplicationForm applicantName={application?.currentApplication?.applicantName} />;
  }

  if (isVerificationSuccess && approvedApplication) {
    return <ApprovedApplicationDetails application={approvedApplication} />;
  }

  if (isVerificationSuccess && rejectedApplication) {
    return (
      <RejectedApplicationDetails
        application={rejectedApplication}
        onReapply={() => {
          setPreviewData(buildPreviewDataFromRejectedApplication(rejectedApplication));
          window.history.pushState({ ...window.history.state, isReapplyingCreatorApplication: true }, '');
          setIsReapplying(true);
        }}
      />
    );
  }

  if (isVerificationSuccess && pendingApplication) {
    return <PendingApplicationDetails application={pendingApplication} />;
  }

  if (isVerificationSuccess && !isApplicationError) {
    return <CreatorApplicationForm applicantName={application?.currentApplication?.applicantName} />;
  }

  if (isVerificationSuccess && isApplicationError) {
    return (
      <VStack minHeight={'100vh'} width={'100vw'} justifyContent={'center'} paddingX={'24px'} spacing={'24px'}>
        <Text variant={'loraTitle'} color={'white'} fontSize={'26px'} lineHeight={'32px'} fontWeight={'400'}>
          Application Unavailable
        </Text>
        <Text
          color={'white'}
          fontSize={{ base: '13px', md: '16px' }}
          fontWeight={'500'}
          lineHeight={{ base: '18px', md: '21px' }}
          textAlign={'center'}
          maxWidth={'520px'}
        >
          We verified your email, but could not load your application details. Please try this link again.
        </Text>
      </VStack>
    );
  }

  return (
    <VStack
      minHeight={'100vh'}
      width={'100%'}
      justifyContent={'start'}
      paddingX={'24px'}
      spacing={'24px'}
      marginTop={'120px'}
    >
      <Text variant={'loraTitle'} color={'white'} fontSize={'26px'} lineHeight={'32px'} fontWeight={'400'}>
        {isVerificationSuccess
          ? 'Email Verified'
          : isVerificationFailed
            ? 'Verification Failed'
            : 'Verifying Your Email'}
      </Text>

      {isVerifying ? <LoadingLogo variant="light" spinnerSize="60px" width="90px" /> : null}

      <Text
        color={'white'}
        fontSize={{ base: '13px', md: '16px' }}
        fontWeight={'500'}
        lineHeight={{ base: '18px', md: '21px' }}
        textAlign={'center'}
        maxWidth={'520px'}
      >
        {isVerificationSuccess
          ? 'Your email has been verified. You can continue your creator application journey from this link.'
          : isVerificationFailed
            ? errorMessage
            : 'Please wait while we verify your creator application link.'}
      </Text>

      {isVerificationFailed ? (
        <CustomButton
          label="Recover application link"
          onClick={() => recoverContentCreatorApplication({ email })}
          isLoading={isRecoveringApplicationLink}
          backgroundColor={'white'}
          labelColor="primary.500"
          hoverOpacityDrop
        />
      ) : null}
    </VStack>
  );
};

export default ContentCreatorApplicationPublicHero;
